import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import {
  Building2,
  CalendarDays,
  FileText,
  ListChecks,
  Plus,
  Search,
  Eye,
  Edit,
  CheckCircle,
  Clock,
  Play,
  AlertTriangle,
  Save,
  X,
  Trash2
} from 'lucide-react';
import { scheduleService } from '../../../src/services/schedule.service';
import { resourceService } from '../../../src/services/resource.service';
import { preConstructionService } from '../../../src/services/pre-construction.service';
import { showSuccess, showError } from '../../../src/utils/notification.utils';
import { notificationService } from '../../../src/services/notification.service';
import { useAuth } from '../../../src/hooks/useAuth';
import { useProjects } from '../../../src/hooks/queries/use-projects';
import { useOrgUsers } from '../../../src/hooks/queries/use-org-users';
import { useSchedules, useAllScheduleTasks } from '../../../src/hooks/queries/use-schedules';

// Helper function to get status badge
export const getStatusBadge = (status: string) => {
  switch (status) {
    case "completed": return <Badge variant="default" className="bg-green-600">Completed</Badge>;
    case "in-progress": return <Badge variant="default">In Progress</Badge>;
    case "not-started": return <Badge variant="secondary">Not Started</Badge>;
    case "overdue": return <Badge variant="destructive">Overdue</Badge>;
    case "upcoming": return <Badge variant="outline">Upcoming</Badge>;
    default: return <Badge variant="outline">{status}</Badge>;
  }
};

// Helper function to get priority badge
export const getPriorityBadge = (priority: string) => {
  switch (priority) {
    case "critical": return <Badge variant="destructive">Critical</Badge>;
    case "high": return <Badge className="bg-orange-600">High</Badge>;
    case "medium": return <Badge variant="secondary">Medium</Badge>;
    case "low": return <Badge variant="outline">Low</Badge>;
    default: return <Badge variant="outline">{priority}</Badge>;
  }
};

// Task Details Modal
export function TaskDetailsModal({ mode, task, trigger, onSuccess }: { mode: 'view' | 'edit'; task: any; trigger: React.ReactNode; onSuccess?: () => void }) {
  const isReadOnly = mode === 'view';
  const [formData, setFormData] = useState({
    taskName: task?.task_name || '',
    project: task?.project_id || '',
    boqItem: task?.boq_item_id || '',
    description: task?.description || '',
    startDate: task?.start_date_planned || '',
    endDate: task?.end_date_planned || '',
    expectedOutput: task?.expected_outcomes || '',
    taskOwner: task?.assigned_to_user_id || '',
    priority: task?.priority || 'medium',
    status: task?.status || 'not-started',
    dependencies: task?.dependencies || '',
    estimatedHours: task?.estimated_hours?.toString() || '',
    resources: task?.resources || '',
    notes: task?.additional_notes || ''
  });

  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const { orgId } = useAuth();
  const { data: projects = [] } = useProjects(orgId);
  const { data: users = [] } = useOrgUsers(orgId);
  const { data: schedules = [] } = useSchedules(orgId);
  const [boqItems, setBoqItems] = useState<any[]>([]);

  // Fetch BOQ items for a project (TaskDetailsModal)
  const fetchBOQItems = async (projectId: string) => {
    if (!projectId) {
      setBoqItems([]);
      return;
    }

    try {
      // Get project details to find linked BOQ
      const project = projects.find(p => p.project_id === projectId);
      if (!project || !project.linked_boq_id) {
        console.log('No linked BOQ found for project:', projectId, 'Project:', project);
        setBoqItems([]);
        return;
      }

      console.log('Fetching BOQ items for BOQ ID:', project.linked_boq_id);
      // Fetch BOQ items from the linked BOQ
      const itemsResponse = await preConstructionService.getBOQItems(project.linked_boq_id);
      const items = itemsResponse.data || [];
      console.log('Fetched BOQ items:', items);
      setBoqItems(Array.isArray(items) ? items : []);
    } catch (error) {
      console.error('Failed to fetch BOQ items:', error);
      setBoqItems([]);
    }
  };

  useEffect(() => {
    if (task?.schedule_id && (schedules as any[]).length > 0 && !formData.project) {
      const taskSchedule = (schedules as any[]).find((s: any) => s.schedule_id === task.schedule_id);
      if (taskSchedule?.project_id) {
        setFormData(prev => ({ ...prev, project: taskSchedule.project_id }));
      }
    }
  }, [schedules]);

  useEffect(() => {
    if (formData.project && (projects as any[]).length > 0) {
      fetchBOQItems(formData.project);
    }
  }, [formData.project, projects]);

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};

    if (!formData.taskName.trim()) {
      newErrors.taskName = 'Task name is required';
    }
    if (!formData.project) {
      newErrors.project = 'Project selection is required';
    }
    if (!formData.boqItem) {
      newErrors.boqItem = 'BOQ Item/Milestone selection is required';
    }
    if (!formData.description.trim()) {
      newErrors.description = 'Task description is required';
    }
    if (!formData.startDate) {
      newErrors.startDate = 'Start date is required';
    }
    if (!formData.endDate) {
      newErrors.endDate = 'End date is required';
    }
    if (!formData.taskOwner) {
      newErrors.taskOwner = 'Task owner is required';
    }
    if (!formData.expectedOutput.trim()) {
      newErrors.expectedOutput = 'Expected output is required';
    }

    // Date validation
    if (formData.startDate && formData.endDate && new Date(formData.startDate) >= new Date(formData.endDate)) {
      newErrors.endDate = 'End date must be after start date';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (mode === 'edit' && validateForm()) {
      try {
        await scheduleService.updateTask(task.schedule_id, task.task_id, {
          task_name: formData.taskName,
          description: formData.description,
          start_date_planned: formData.startDate,
          end_date_planned: formData.endDate,
          expected_outcomes: formData.expectedOutput,
          assigned_to_user_id: formData.taskOwner,
          priority: formData.priority,
          estimated_hours: formData.estimatedHours ? parseInt(formData.estimatedHours) : undefined,
          additional_notes: formData.notes,
          organization_id: orgId
        });

        showSuccess({
          title: 'Task Updated',
          message: 'Task has been updated successfully'
        });

        if (onSuccess) onSuccess();
      } catch (error) {
        console.error('Failed to update task:', error);
        showError({
          title: 'Update Failed',
          message: 'Failed to update task. Please try again.'
        });
      }
    }
  };

  // Get display names
  const projectName = projects.find(p => p.project_id === formData.project)?.project_name || formData.project || 'N/A';
  const ownerUser = users.find(u => u.user_id === formData.taskOwner);
  const ownerName = ownerUser ? `${ownerUser.first_name || ''} ${ownerUser.last_name || ''}`.trim() || ownerUser.username : formData.taskOwner || 'N/A';

  return (
    <Dialog>
      <DialogTrigger asChild>
        {trigger}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto modal-content">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {mode === 'view' ? <Eye className="h-5 w-5" /> : <Edit className="h-5 w-5" />}
            {mode === 'view' ? 'View Task Details' : 'Edit Task Details'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'view' ? 'View complete task information' : 'Update task information'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Basic Task Information */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <ListChecks className="h-4 w-4 text-primary" />
              <h4 className="font-medium">Task Information</h4>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="task-name">Task Name *</Label>
                <Input
                  id="task-name"
                  value={formData.taskName}
                  onChange={(e) => setFormData({...formData, taskName: e.target.value})}
                  readOnly={isReadOnly}
                  className={isReadOnly ? 'bg-muted' : ''}
                  placeholder="e.g., Foundation Excavation"
                />
                {errors.taskName && <p className="text-sm text-destructive">{errors.taskName}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="task-priority">Priority</Label>
                {isReadOnly ? (
                  <Input
                    id="task-priority"
                    value={formData.priority.charAt(0).toUpperCase() + formData.priority.slice(1)}
                    readOnly={isReadOnly}
                    className="bg-muted"
                  />
                ) : (
                  <Select value={formData.priority} onValueChange={(value) => setFormData({...formData, priority: value})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="task-description">Task Description *</Label>
              <Textarea
                id="task-description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                readOnly={isReadOnly}
                className={isReadOnly ? 'bg-muted' : ''}
                placeholder="Provide detailed description of the task, requirements, and deliverables..."
                rows={3}
              />
              {errors.description && <p className="text-sm text-destructive">{errors.description}</p>}
            </div>
          </div>

          {/* Project & Milestone Linkage */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-primary" />
              <h4 className="font-medium">Project & Milestone Linkage</h4>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="linked-project">Linked Project *</Label>
                {isReadOnly ? (
                  <Input
                    id="linked-project"
                    value={projectName}
                    readOnly={isReadOnly}
                    className="bg-muted"
                  />
                ) : (
                  <Select
                    value={formData.project}
                    onValueChange={(value) => {
                      setFormData({...formData, project: value, boqItem: ''});
                      fetchBOQItems(value);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select project" />
                    </SelectTrigger>
                    <SelectContent>
                      {projects.map((project) => (
                        <SelectItem key={project.project_id} value={project.project_id}>
                          {project.project_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {errors.project && <p className="text-sm text-destructive">{errors.project}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="boq-milestone">BOQ Item/Milestone *</Label>
                {isReadOnly ? (
                  <Input
                    id="boq-milestone"
                    value={
                      boqItems.length > 0
                        ? (() => {
                            const item = boqItems.find(b => b.boq_item_id === formData.boqItem);
                            return item ? `${item.item_number} - ${item.description}` : formData.boqItem || 'N/A';
                          })()
                        : (formData.boqItem || 'N/A')
                    }
                    readOnly={isReadOnly}
                    className="bg-muted"
                  />
                ) : (
                  <Select value={formData.boqItem} onValueChange={(value) => setFormData({...formData, boqItem: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select BOQ item" />
                    </SelectTrigger>
                    <SelectContent>
                      {boqItems.length > 0 ? (
                        boqItems.map((item) => (
                          <SelectItem key={item.boq_item_id} value={item.boq_item_id}>
                            {item.item_number} - {item.description}
                          </SelectItem>
                        ))
                      ) : (
                        <div className="p-2 text-sm text-muted-foreground text-center">No BOQ items available</div>
                      )}
                    </SelectContent>
                  </Select>
                )}
                {errors.boqItem && <p className="text-sm text-destructive">{errors.boqItem}</p>}
              </div>
            </div>
          </div>

          {/* Timeline & Ownership */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <CalendarDays className="h-4 w-4 text-primary" />
              <h4 className="font-medium">Timeline & Ownership</h4>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start-date">Planned Start Date *</Label>
                <Input
                  id="start-date"
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData({...formData, startDate: e.target.value})}
                  readOnly={isReadOnly}
                  className={isReadOnly ? 'bg-muted' : ''}
                />
                {errors.startDate && <p className="text-sm text-destructive">{errors.startDate}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="end-date">End Date *</Label>
                <Input
                  id="end-date"
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => setFormData({...formData, endDate: e.target.value})}
                  readOnly={isReadOnly}
                  className={isReadOnly ? 'bg-muted' : ''}
                />
                {errors.endDate && <p className="text-sm text-destructive">{errors.endDate}</p>}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="task-owner">Task Owner *</Label>
                {isReadOnly ? (
                  <Input
                    id="task-owner"
                    value={ownerName}
                    readOnly={isReadOnly}
                    className="bg-muted"
                  />
                ) : (
                  <Select value={formData.taskOwner} onValueChange={(value) => setFormData({...formData, taskOwner: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select task owner" />
                    </SelectTrigger>
                    <SelectContent>
                      {users.map((user) => (
                        <SelectItem key={user.user_id} value={user.user_id}>
                          {`${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {errors.taskOwner && <p className="text-sm text-destructive">{errors.taskOwner}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="estimated-hours">Estimated Hours</Label>
                <Input
                  id="estimated-hours"
                  type="number"
                  value={formData.estimatedHours}
                  onChange={(e) => setFormData({...formData, estimatedHours: e.target.value})}
                  readOnly={isReadOnly}
                  className={isReadOnly ? 'bg-muted' : ''}
                  placeholder="0"
                  min="0"
                />
              </div>
            </div>
          </div>

          {/* Task Details */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-primary" />
              <h4 className="font-medium">Task Details</h4>
            </div>

            <div className="space-y-2">
              <Label htmlFor="expected-output">Expected Output *</Label>
              <Textarea
                id="expected-output"
                value={formData.expectedOutput}
                onChange={(e) => setFormData({...formData, expectedOutput: e.target.value})}
                readOnly={isReadOnly}
                className={isReadOnly ? 'bg-muted' : ''}
                placeholder="Describe the expected deliverable, outcome, or completion criteria..."
                rows={2}
              />
              {errors.expectedOutput && <p className="text-sm text-destructive">{errors.expectedOutput}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="task-dependencies">Dependencies (Optional)</Label>
              <Input
                id="task-dependencies"
                value={formData.dependencies}
                onChange={(e) => setFormData({...formData, dependencies: e.target.value})}
                readOnly={isReadOnly}
                className={isReadOnly ? 'bg-muted' : ''}
                placeholder="e.g., Site survey completion, Permit approval"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="required-resources">Required Resources (Optional)</Label>
              <Input
                id="required-resources"
                value={formData.resources}
                onChange={(e) => setFormData({...formData, resources: e.target.value})}
                readOnly={isReadOnly}
                className={isReadOnly ? 'bg-muted' : ''}
                placeholder="e.g., Excavator, 3 workers, Steel bars"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="task-notes">Additional Notes (Optional)</Label>
              <Textarea
                id="task-notes"
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                readOnly={isReadOnly}
                className={isReadOnly ? 'bg-muted' : ''}
                placeholder="Any additional information, special instructions, or considerations..."
                rows={2}
              />
            </div>

            {isReadOnly && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="task-status">Current Status</Label>
                  <Input
                    id="task-status"
                    value={formData.status.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    readOnly={isReadOnly}
                    className="bg-muted"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="task-progress">Progress</Label>
                  <Input
                    id="task-progress"
                    value={`${task?.progress || 0}%`}
                    readOnly={isReadOnly}
                    className="bg-muted"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
          {mode === 'edit' && <Button onClick={handleSave}>Save Changes</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Task Status Update Modal
export function TaskStatusUpdateModal({ task, action, trigger, onSuccess }: { task: any; action: 'start' | 'complete'; trigger: React.ReactNode; onSuccess?: () => void }) {
  const [notes, setNotes] = useState('');
  const { orgId } = useAuth();
  const { data: projects = [] } = useProjects(orgId);
  const { data: users = [] } = useOrgUsers(orgId);

  const projectName = (projects as any[]).find((p: any) => p.project_id === task.project_id)?.project_name || 'Unknown Project';
  const owner = (users as any[]).find((u: any) => u.user_id === task.assigned_to_user_id);
  const ownerName = owner
    ? `${owner.first_name || ''} ${owner.last_name || ''}`.trim() || owner.username || 'Unassigned'
    : 'Unassigned';

  const handleStatusUpdate = async () => {
    try {
      if (action === 'start') {
        await scheduleService.startTask(task.schedule_id, task.task_id, {
          notes,
          start_date_actual: new Date().toISOString().split('T')[0]
        });

        showSuccess({
          title: 'Task Started',
          message: 'Task has been marked as in progress'
        });
      } else {
        await scheduleService.completeTask(task.schedule_id, task.task_id, {
          notes,
          completion_notes: notes,
          end_date_actual: new Date().toISOString().split('T')[0]
        });

        showSuccess({
          title: 'Task Completed',
          message: 'Task has been marked as completed'
        });
      }

      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Failed to update task status:', error);
      showError({
        title: 'Update Failed',
        message: 'Failed to update task status. Please try again.'
      });
    }
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        {trigger}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px] modal-content">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {action === 'start' ? (
              <>
                <Clock className="h-5 w-5 text-blue-600" />
                Start Task
              </>
            ) : (
              <>
                <CheckCircle className="h-5 w-5 text-green-600" />
                Complete Task
              </>
            )}
          </DialogTitle>
          <DialogDescription>
            {action === 'start'
              ? 'Mark this task as started and in progress'
              : 'Mark this task as completed'
            }
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="p-4 bg-muted/50 rounded-lg">
            <h4 className="font-medium">{task.task_name}</h4>
            <p className="text-sm text-muted-foreground mt-1">
              Project: {projectName} • Owner: {ownerName}
            </p>
            <p className="text-sm text-muted-foreground">
              Timeline: {task.start_date_planned || 'N/A'} to {task.end_date_planned || 'N/A'}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="status-notes">
              {action === 'start' ? 'Start Notes (Optional)' : 'Completion Notes (Optional)'}
            </Label>
            <Textarea
              id="status-notes"
              placeholder={
                action === 'start'
                  ? "Add any notes about starting this task..."
                  : "Add completion notes, lessons learned, or additional information..."
              }
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          {action === 'start' && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-2">
                <Clock className="h-4 w-4 text-blue-600 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-blue-900">Task Start</p>
                  <p className="text-blue-700">
                    This task will be marked as in progress and will appear in active task lists.
                  </p>
                </div>
              </div>
            </div>
          )}

          {action === 'complete' && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-600 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-green-900">Task Completion</p>
                  <p className="text-green-700">
                    This task will be marked as completed and will contribute to project progress.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
          <Button
            onClick={handleStatusUpdate}
            className={action === 'start' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700'}
          >
            {action === 'start' ? (
              <>
                <Clock className="h-4 w-4 mr-2" />
                Start Task
              </>
            ) : (
              <>
                <CheckCircle className="h-4 w-4 mr-2" />
                Complete Task
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// New Task Modal Component
export function NewTaskModal({ onSuccess }: { onSuccess?: () => void }) {
  const [open, setOpen] = useState(false);
  const { orgId } = useAuth();
  const { data: projects = [] } = useProjects(orgId);
  const { data: users = [] } = useOrgUsers(orgId);
  const { data: schedules = [] } = useSchedules(orgId);
  const scheduleIds = (schedules as any[]).map((s: any) => s.schedule_id);
  const { data: tasks = [] } = useAllScheduleTasks(scheduleIds);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    taskName: '',
    schedule: '',
    project: '',
    boqItem: '',
    description: '',
    startDate: '',
    endDate: '',
    expectedOutput: '',
    taskOwner: '',
    priority: 'medium',
    status: 'not-started',
    dependencies: '',
    estimatedHours: '',
    resources: '',
    notes: ''
  });

  const [taskDependencies, setTaskDependencies] = useState<Array<{
    id: string;
    predecessor_task_id: string;
    dependency_type: string;
    lag_days: number;
  }>>([]);

  const [taskResources, setTaskResources] = useState<Array<{
    id: string;
    resource_id: string;
    resource_type: string;
    quantity_required: number;
    unit_of_measure: string;
  }>>([]);

  const [availableResources, setAvailableResources] = useState<any[]>([]);
  const [boqItems, setBoqItems] = useState<any[]>([]);

  // Fetch BOQ items for a project (NewTaskModal)
  const fetchBOQItems = async (projectId: string) => {
    if (!projectId) {
      setBoqItems([]);
      return;
    }

    try {
      // Get project details to find linked BOQ
      const project = projects.find(p => p.project_id === projectId);
      if (!project || !project.linked_boq_id) {
        console.log('No linked BOQ found for project:', projectId, 'Project:', project);
        setBoqItems([]);
        return;
      }

      console.log('Fetching BOQ items for BOQ ID:', project.linked_boq_id);
      // Fetch BOQ items from the linked BOQ
      const itemsResponse = await preConstructionService.getBOQItems(project.linked_boq_id);
      const items = itemsResponse.data || [];
      console.log('Fetched BOQ items:', items);
      setBoqItems(Array.isArray(items) ? items : []);
    } catch (error) {
      console.error('Failed to fetch BOQ items:', error);
      setBoqItems([]);
    }
  };

  useEffect(() => {
    if (!open || !orgId) return;
    resourceService.getResources(orgId, 1, 100)
      .then(res => {
        const data = (res.data as any)?.data || res.data || [];
        setAvailableResources(Array.isArray(data) ? data : []);
      })
      .catch(err => console.error('Failed to fetch resources:', err));
  }, [open, orgId]);

  const [errors, setErrors] = useState<{[key: string]: string}>({});

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};

    if (!formData.taskName.trim()) {
      newErrors.taskName = 'Task name is required';
    }
    if (!formData.project) {
      newErrors.project = 'Project selection is required';
    }
    if (!formData.boqItem) {
      newErrors.boqItem = 'BOQ Item/Milestone selection is required';
    }
    if (!formData.description.trim()) {
      newErrors.description = 'Task description is required';
    }
    if (!formData.startDate) {
      newErrors.startDate = 'Start date is required';
    }
    if (!formData.endDate) {
      newErrors.endDate = 'End date is required';
    }
    if (!formData.taskOwner) {
      newErrors.taskOwner = 'Task owner is required';
    }
    if (!formData.expectedOutput.trim()) {
      newErrors.expectedOutput = 'Expected output is required';
    }

    // Date validation
    if (formData.startDate && formData.endDate && new Date(formData.startDate) >= new Date(formData.endDate)) {
      newErrors.endDate = 'End date must be after start date';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const createdTaskResponse = await scheduleService.createTask(formData.schedule, {
        task_name: formData.taskName,
        description: formData.description,
        start_date_planned: formData.startDate,
        end_date_planned: formData.endDate,
        assigned_to_user_id: formData.taskOwner,
        status: 'Not Started',
        progress_percentage: '0',
        organization_id: orgId
      });

      const createdTask = createdTaskResponse.data;
      const taskId = createdTask?.task_id;

      // Create task dependencies
      if (taskId && taskDependencies.length > 0) {
        for (const dep of taskDependencies) {
          if (dep.predecessor_task_id) {
            try {
              await scheduleService.createTaskDependencyForTask(
                formData.schedule,
                taskId,
                {
                  organization_id: orgId,
                  predecessor_task_id: dep.predecessor_task_id,
                  dependency_type: dep.dependency_type as any,
                  lag_days: dep.lag_days
                }
              );
            } catch (depErr) {
              console.error('Failed to create dependency:', depErr);
            }
          }
        }
      }

      // Create task resources
      if (taskId && taskResources.length > 0) {
        for (const res of taskResources) {
          if (res.resource_id) {
            try {
              await resourceService.addTaskResource(taskId, {
                organization_id: orgId,
                resource_id: res.resource_id,
                quantity_required: res.quantity_required,
                unit_of_measure: res.unit_of_measure,
                allocation_status: 'Planned'
              });
            } catch (resErr) {
              console.error('Failed to add resource:', resErr);
            }
          }
        }
      }

      showSuccess({
        title: 'Task Created',
        message: 'Task has been created successfully with dependencies and resources'
      });

      if (formData.taskOwner) {
        const projectName = (projects as any[]).find((p: any) => p.project_id === formData.project)?.project_name ?? 'your project';
        notificationService.taskCreated(formData.taskOwner, formData.taskName, projectName);
      }

      setOpen(false);
      setFormData({
        taskName: '',
        schedule: '',
        project: '',
        boqItem: '',
        description: '',
        startDate: '',
        endDate: '',
        expectedOutput: '',
        taskOwner: '',
        priority: 'medium',
        status: 'not-started',
        dependencies: '',
        estimatedHours: '',
        resources: '',
        notes: ''
      });
      setTaskDependencies([]);
      setTaskResources([]);

      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Failed to create task:', error);
      showError({
        title: 'Creation Failed',
        message: 'Failed to create task. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          New Task
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto modal-content">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ListChecks className="h-5 w-5" />
            Create New Task
          </DialogTitle>
          <DialogDescription>
            Create a new task with project linkage and milestone tracking
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Basic Task Information */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <ListChecks className="h-4 w-4 text-primary" />
              <h4 className="font-medium">Task Information</h4>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="task-name">Task Name *</Label>
                <Input
                  id="task-name"
                  placeholder="e.g., Foundation Excavation"
                  value={formData.taskName}
                  onChange={(e) => setFormData({...formData, taskName: e.target.value})}
                />
                {errors.taskName && <p className="text-sm text-destructive">{errors.taskName}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="task-priority">Priority</Label>
                <Select value={formData.priority} onValueChange={(value) => setFormData({...formData, priority: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="task-description">Task Description *</Label>
              <Textarea
                id="task-description"
                placeholder="Provide detailed description of the task, requirements, and deliverables..."
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
              />
              {errors.description && <p className="text-sm text-destructive">{errors.description}</p>}
            </div>
          </div>

          {/* Project & Milestone Linkage */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-primary" />
              <h4 className="font-medium">Project & Milestone Linkage</h4>
            </div>

            <div className="space-y-2">
              <Label htmlFor="linked-schedule">Linked Schedule/Milestone *</Label>
              <Select
                value={formData.schedule}
                onValueChange={(value) => {
                  setFormData({...formData, schedule: value, project: '', boqItem: ''});
                  // Auto-load project from schedule
                  const selectedSchedule = schedules.find(s => s.schedule_id === value);
                  if (selectedSchedule?.project_id) {
                    setFormData(prev => ({ ...prev, schedule: value, project: selectedSchedule.project_id, boqItem: '' }));
                    fetchBOQItems(selectedSchedule.project_id);
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select schedule" />
                </SelectTrigger>
                <SelectContent>
                  {schedules.map((schedule) => (
                    <SelectItem key={schedule.schedule_id} value={schedule.schedule_id}>
                      {schedule.schedule_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.schedule && <p className="text-sm text-destructive">{errors.schedule}</p>}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="linked-project">Linked Project *</Label>
                <Select
                  value={formData.project}
                  onValueChange={(value) => {
                    setFormData({...formData, project: value, boqItem: ''});
                    fetchBOQItems(value);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select project" />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map((project) => (
                      <SelectItem key={project.project_id} value={project.project_id}>
                        {project.project_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.project && <p className="text-sm text-destructive">{errors.project}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="boq-milestone">BOQ Item/Milestone *</Label>
                <Select value={formData.boqItem} onValueChange={(value) => setFormData({...formData, boqItem: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select BOQ item" />
                  </SelectTrigger>
                  <SelectContent>
                    {boqItems.length > 0 ? (
                      boqItems.map((item) => (
                        <SelectItem key={item.boq_item_id} value={item.boq_item_id}>
                          {item.item_number} - {item.description}
                        </SelectItem>
                      ))
                    ) : (
                      <div className="p-2 text-sm text-muted-foreground text-center">No BOQ items available</div>
                    )}
                  </SelectContent>
                </Select>
                {errors.boqItem && <p className="text-sm text-destructive">{errors.boqItem}</p>}
              </div>
            </div>
          </div>

          {/* Timeline & Ownership */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <CalendarDays className="h-4 w-4 text-primary" />
              <h4 className="font-medium">Timeline & Ownership</h4>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start-date">Planned Start Date *</Label>
                <Input
                  id="start-date"
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData({...formData, startDate: e.target.value})}
                />
                {errors.startDate && <p className="text-sm text-destructive">{errors.startDate}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="end-date">End Date *</Label>
                <Input
                  id="end-date"
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => setFormData({...formData, endDate: e.target.value})}
                />
                {errors.endDate && <p className="text-sm text-destructive">{errors.endDate}</p>}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="task-owner">Task Owner *</Label>
                <Select value={formData.taskOwner} onValueChange={(value) => setFormData({...formData, taskOwner: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select task owner" />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map((user) => (
                      <SelectItem key={user.user_id} value={user.user_id}>
                        {user.username || `${user.first_name} ${user.last_name}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.taskOwner && <p className="text-sm text-destructive">{errors.taskOwner}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="estimated-hours">Estimated Hours</Label>
                <Input
                  id="estimated-hours"
                  type="number"
                  placeholder="0"
                  min="0"
                  value={formData.estimatedHours}
                  onChange={(e) => setFormData({...formData, estimatedHours: e.target.value})}
                />
              </div>
            </div>
          </div>

          {/* Task Details */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-primary" />
              <h4 className="font-medium">Task Details</h4>
            </div>

            <div className="space-y-2">
              <Label htmlFor="expected-output">Expected Output *</Label>
              <Textarea
                id="expected-output"
                placeholder="Describe the expected deliverable, outcome, or completion criteria..."
                rows={2}
                value={formData.expectedOutput}
                onChange={(e) => setFormData({...formData, expectedOutput: e.target.value})}
              />
              {errors.expectedOutput && <p className="text-sm text-destructive">{errors.expectedOutput}</p>}
            </div>

            {/* Task Dependencies */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Task Dependencies</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setTaskDependencies([...taskDependencies, {
                      id: Date.now().toString(),
                      predecessor_task_id: '',
                      dependency_type: 'Finish to Start',
                      lag_days: 0
                    }]);
                  }}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Dependency
                </Button>
              </div>
              {taskDependencies.map((dep, index) => (
                <div key={dep.id} className="flex items-center gap-2 p-3 border rounded-lg">
                  <div className="flex-1 grid grid-cols-3 gap-2">
                    <Select
                      value={dep.predecessor_task_id}
                      onValueChange={(value) => {
                        const updated = [...taskDependencies];
                        updated[index].predecessor_task_id = value;
                        setTaskDependencies(updated);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select task" />
                      </SelectTrigger>
                      <SelectContent>
                        {tasks.map((t: any) => (
                          <SelectItem key={t.task_id} value={t.task_id}>
                            {t.task_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select
                      value={dep.dependency_type}
                      onValueChange={(value) => {
                        const updated = [...taskDependencies];
                        updated[index].dependency_type = value;
                        setTaskDependencies(updated);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Finish to Start">Finish to Start</SelectItem>
                        <SelectItem value="Start to Start">Start to Start</SelectItem>
                        <SelectItem value="Finish to Finish">Finish to Finish</SelectItem>
                        <SelectItem value="Start to Finish">Start to Finish</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      type="number"
                      placeholder="Lag days"
                      value={dep.lag_days}
                      onChange={(e) => {
                        const updated = [...taskDependencies];
                        updated[index].lag_days = parseInt(e.target.value) || 0;
                        setTaskDependencies(updated);
                      }}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setTaskDependencies(taskDependencies.filter((_, i) => i !== index));
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>

            {/* Task Resources */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Required Resources</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setTaskResources([...taskResources, {
                      id: Date.now().toString(),
                      resource_id: '',
                      resource_type: 'Labor',
                      quantity_required: 1,
                      unit_of_measure: ''
                    }]);
                  }}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Resource
                </Button>
              </div>
              {taskResources.map((res, index) => (
                <div key={res.id} className="flex items-center gap-2 p-3 border rounded-lg">
                  <div className="flex-1 grid grid-cols-4 gap-2">
                    <Select
                      value={res.resource_id}
                      onValueChange={(value) => {
                        const updated = [...taskResources];
                        updated[index].resource_id = value;
                        const selectedResource = availableResources.find(r => r.resource_id === value);
                        if (selectedResource) {
                          updated[index].resource_type = selectedResource.resource_type;
                          updated[index].unit_of_measure = selectedResource.unit_of_measure || '';
                        }
                        setTaskResources(updated);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select resource" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableResources.map((r) => (
                          <SelectItem key={r.resource_id} value={r.resource_id}>
                            {r.resource_name} ({r.resource_type})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      type="text"
                      placeholder="Type"
                      value={res.resource_type}
                      readOnly
                      className="bg-muted"
                    />
                    <Input
                      type="number"
                      placeholder="Quantity"
                      min="1"
                      value={res.quantity_required}
                      onChange={(e) => {
                        const updated = [...taskResources];
                        updated[index].quantity_required = parseInt(e.target.value) || 1;
                        setTaskResources(updated);
                      }}
                    />
                    <Input
                      type="text"
                      placeholder="Unit"
                      value={res.unit_of_measure}
                      onChange={(e) => {
                        const updated = [...taskResources];
                        updated[index].unit_of_measure = e.target.value;
                        setTaskResources(updated);
                      }}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setTaskResources(taskResources.filter((_, i) => i !== index));
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>

            <div className="space-y-2">
              <Label htmlFor="task-notes">Additional Notes (Optional)</Label>
              <Textarea
                id="task-notes"
                placeholder="Any additional information, special instructions, or considerations..."
                rows={2}
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={loading}>
            {loading ? (
              <>Loading...</>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Create Task
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
